import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { BuyPackage } from '../../../../common/buyPackage'
import useDiscountOrder from '../../../../common/category/useDiscountOrder'
import useGetVAT from '../../../../common/category/useVAT'
import useDidUpdateEffect from '../../../../common/hooks/useDidUpdateEffect'
import { Checkbox } from '../../../../common/html/checkbox'
import { Element } from '../../../../common/html/element'
import { Input } from '../../../../common/html/input'
import { PopupAction } from '../../../../common/popup/PopupAction'
import { debounce } from '../../../../helpers/common'
import {
  formatCurrency,
  formatText,
  formatVal,
  valToPercent,
} from '../../../../helpers/value'
import { CURRENCY } from '../../../../utils/currency'
import { SCREENING_REPORT_TYPE_ID } from '../../common/constant'
import {
  changeDiscountInfo,
  selectDiscountInfo,
  selectNumberOfCompany,
  selectScreeningPrice,
} from '../store/slice'
import { getScreeningPrice, getVoucher } from '../store/thunk'
import useGetExchangeRates from './../../../../common/category/useGetExchangeRates'
import { PriceConversion } from './PriceConvertion'
import classes from './index.module.scss'

export const RADIO_TRADE_DISCOUNT = {
  DISCOUNT: 'discount',
  VOUCHER: 'voucher',
}

export const PaymentInfo = () => {
  const dispatch = useDispatch()

  const locale = useSelector((state) => state.i18n.locale)
  const { exchangeRates } = useGetExchangeRates()
  const { vatPercentage } = useGetVAT()
  const { discountOrder } = useDiscountOrder()
  const numberOfCompany = useSelector(selectNumberOfCompany) || 0
  const screeningPrice = useSelector(selectScreeningPrice)
  const discountInfo = useSelector(selectDiscountInfo)

  const originUnitPrice = screeningPrice?.price
  const discountUnitPrice =
    screeningPrice?.price * (1 - screeningPrice?.discountPercentage)
  const isDiscountUnitPriceValid =
    typeof discountUnitPrice === 'number' && !isNaN(discountUnitPrice)
  const price =
    (isDiscountUnitPriceValid ? discountUnitPrice : originUnitPrice) *
    numberOfCompany
  const discountOrderPrice =
    (discountInfo?.typeRadio && discountInfo?.percentageVoucher
      ? price * discountInfo?.percentageVoucher
      : 0) + (price * discountOrder?.[0]?.discountPercentage || 0)
  const VAT = Math.max(0, (price - discountOrderPrice) * vatPercentage)
  const totalPrice = Math.max(0, price - discountOrderPrice + VAT)

  useEffect(() => {
    dispatch(getScreeningPrice())
  }, [])

  useEffect(() => {
    dispatch(
      changeDiscountInfo({
        ...discountOrder?.[0],
      }),
    )
  }, [discountOrder])

  return (
    <Element className={classes.paymentInfo}>
      <Element
        className={classes.header}
        style={{
          fontSize: 14,
          color: '#191D25',
          backgroundColor: '#D5D5D5',
        }}
      >
        <Translate value="screening.PAYMENT_INFORMATION" />
      </Element>

      <Element
        className={classes.content}
        style={{ backgroundColor: '#F3F3F3' }}
      >
        <table className="w100">
          <tbody style={{ verticalAlign: 'baseline' }}>
            <tr>
              <td className="fw600">
                <Translate value="screening.VIEW_COUNT" />
              </td>
              <td>
                {formatVal(numberOfCompany, locale, false, 0)}{' '}
                <Translate
                  value="screening.COUNT_RESULT"
                  suffix={numberOfCompany > 1 ? 's' : ''}
                />
              </td>
            </tr>
            <tr>
              <td className="fw600">
                <Translate value="screening.UNIT_PRICE" />
              </td>
              <td>
                <div
                  style={{
                    color: isDiscountUnitPriceValid ? '#9C9C9C' : '',
                    textDecoration: isDiscountUnitPriceValid
                      ? 'line-through'
                      : '',
                  }}
                >
                  {formatCurrency(
                    originUnitPrice,
                    CURRENCY.USD,
                    CURRENCY.VND,
                    exchangeRates,
                    locale,
                  )}
                  &nbsp;/&nbsp;
                  <Translate value="screening.COUNT_RESULT" suffix="" />
                </div>
                {isDiscountUnitPriceValid && (
                  <div>
                    {formatCurrency(
                      discountUnitPrice,
                      CURRENCY.USD,
                      CURRENCY.VND,
                      exchangeRates,
                      locale,
                    )}
                    &nbsp;/&nbsp;
                    <Translate value="screening.COUNT_RESULT" suffix="" />
                  </div>
                )}
              </td>
              <td className="flexColumn">
                <div
                  style={{
                    opacity: isDiscountUnitPriceValid ? 0 : 1,
                    visibility: isDiscountUnitPriceValid ? 'hidden' : '',
                  }}
                >
                  <PriceConversion value={originUnitPrice} />
                </div>
                {isDiscountUnitPriceValid && (
                  <div>
                    <PriceConversion value={discountUnitPrice} />
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td className="fw600">
                <Translate value="screening.PRICE" />
              </td>
              <td>
                {formatCurrency(
                  price,
                  CURRENCY.USD,
                  CURRENCY.VND,
                  exchangeRates,
                  locale,
                )}
              </td>
              <td>
                <PriceConversion value={price} />
              </td>
            </tr>
            <tr>
              <td className="fw600">
                <Translate value="screening.TRADE_DISCOUNT" />
                <SelectDiscount
                  price={price}
                  discountOrder={discountOrder?.[0]}
                  discountInfo={discountInfo}
                />
              </td>
              <td>
                {formatCurrency(
                  discountOrderPrice,
                  CURRENCY.USD,
                  CURRENCY.VND,
                  exchangeRates,
                  locale,
                )}
              </td>
              <td>
                <PriceConversion value={discountOrderPrice} />
              </td>
            </tr>
            <tr>
              <td>
                <Translate value="screening.VAT" className="fw600" />
              </td>
              <td>
                {formatCurrency(
                  VAT,
                  CURRENCY.USD,
                  CURRENCY.VND,
                  exchangeRates,
                  locale,
                )}
              </td>
              <td>
                <PriceConversion value={VAT} />
              </td>
            </tr>
            <tr>
              <td>
                <Translate value="screening.TOTAL_PAYMENT" className="fw600" />
              </td>
              <td>
                {formatCurrency(
                  totalPrice,
                  CURRENCY.USD,
                  CURRENCY.VND,
                  exchangeRates,
                  locale,
                )}
              </td>
              <td>
                <PriceConversion value={totalPrice} />
              </td>
            </tr>
          </tbody>
        </table>

        <Element className="mt1">
          <BuyPackage
            fiinGateReportTypeId={SCREENING_REPORT_TYPE_ID}
            isTwoLine={false}
          />
        </Element>
      </Element>
    </Element>
  )
}

const SelectDiscount = ({ price, discountOrder, discountInfo }) => {
  const dispatch = useDispatch()
  const locale = useSelector((state) => state.i18n.locale)
  const { exchangeRates } = useGetExchangeRates()

  const [isShow, setIsShow] = useState(false)
  const [code, setCode] = useState('')
  const [isValid, setValid] = useState(false)
  const [dataVoucher, setDataVoucher] = useState({})

  const onOpen = () => {
    setIsShow(true)
  }

  const onClose = () => {
    setIsShow(false)
  }

  const onChangeCode = (e) => {
    setCode(e.target.value)
  }

  const handleChangeText = useCallback(
    debounce((value) => {
      dispatch(
        getVoucher({
          Code: value,
        }),
      )
        .unwrap()
        .then((value) => {
          setValid(false)
          setDataVoucher(value)
        })
        .catch(() => {
          setDataVoucher({})
          setValid(true)
        })
    }, 700),
    [],
  )

  const onSubmit = () => {
    if (
      (!dataVoucher || !Object.keys(dataVoucher).length) &&
      discountInfo?.typeRadio
    )
      return
    dispatch(
      changeDiscountInfo({
        percentageVoucher: dataVoucher?.discountPercentage,
        voucherCode: dataVoucher?.code,
      }),
    )
    onClose()
  }

  const onChangeCheckbox = (value) => {
    dispatch(
      changeDiscountInfo({
        typeRadio: value,
      }),
    )
  }

  useDidUpdateEffect(() => {
    if (code?.trim()) {
      handleChangeText(code)
    }
  }, [code])

  return (
    <>
      <Element
        onClick={onOpen}
        className="pl2 pt1 fstItalic fw400 cursorPointer"
        style={{ color: '#2C73E8' }}
      >
        <Translate value="screening.SELECT_OR_ENTER_CODE" />
      </Element>

      <PopupAction
        title="cartAndOrders.tradeDiscount.select"
        isShow={isShow}
        handleClose={onClose}
        btnSubmit={{
          label: 'common.APPLY',
          event: onSubmit,
          isI18n: true,
        }}
        btnCancel={{
          label: 'common.CANCEL',
          event: onClose,
          isI18n: true,
        }}
      >
        <div className="fw600">
          {!!discountOrder && (
            <div className={`dFlex justifyContentBetween alignItemsCenter pb2`}>
              <Checkbox
                value={RADIO_TRADE_DISCOUNT.DISCOUNT}
                checked={true}
                onChange={() => {}}
                title={formatText(discountOrder.name)}
                mlTitle={5}
                isI18n={false}
                styleIcon={{
                  opacity: 0.6,
                }}
              />
              <div>
                {valToPercent(
                  discountOrder.discountPercentage,
                  locale,
                  false,
                  false,
                  0,
                )}
                {' ~ '}
                {formatCurrency(
                  price * discountOrder.discountPercentage,
                  CURRENCY.USD,
                  CURRENCY.VND,
                  exchangeRates,
                  locale,
                )}
              </div>
            </div>
          )}

          <div className={`dFlex justifyContentBetween alignItemsCenter pb2`}>
            <div className="dFlex w100 alignItemsCenter">
              {!!discountOrder ? (
                <>
                  <Checkbox
                    value={RADIO_TRADE_DISCOUNT.VOUCHER}
                    checked={discountInfo.typeRadio}
                    onChange={onChangeCheckbox}
                    title="Voucher"
                    mlTitle={5}
                    isI18n={false}
                    titleStyle={{ marginRight: 8 }}
                  />
                </>
              ) : (
                <span>Voucher</span>
              )}
              &ensp;
              {discountInfo.typeRadio && (
                <Input
                  containerStyle={{ flex: 1 }}
                  placeHolder="cartAndOrders.tradeDiscount.enter"
                  value={code}
                  onChange={onChangeCode}
                  isShowIcon={false}
                  isValid={!isValid}
                  textInvalid="cartAndOrders.message.m19"
                />
              )}
            </div>
            {discountInfo.typeRadio && !!dataVoucher?.discountPercentage && (
              <div className="whiteSpaceNowrap ml1">
                {valToPercent(
                  dataVoucher?.discountPercentage,
                  locale,
                  false,
                  false,
                  0,
                )}
                {' ~ '}
                {formatCurrency(
                  price * dataVoucher?.discountPercentage,
                  CURRENCY.USD,
                  CURRENCY.VND,
                  exchangeRates,
                  locale,
                )}
              </div>
            )}
            {discountInfo.typeRadio && !!dataVoucher?.discountAmount && (
              <div className="whiteSpaceNowrap ml1">
                {formatCurrency(
                  dataVoucher?.discountAmount,
                  CURRENCY.USD,
                  CURRENCY.VND,
                  exchangeRates,
                  locale,
                )}
              </div>
            )}
          </div>
        </div>
      </PopupAction>
    </>
  )
}
